import qs from "qs";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { deleteInternalReferralRequisitionDraft } from "services/api/taptype/internalReferrals";
import { deleteOrderRequisitionDraft } from "services/api/taptype/orders";
import { actions as flashesActions } from "store/flashes";
import { apiHandler, errorResponseInterceptor, getRouteLocale, isFunction } from "utils/helpers";
import { clearModalArtifacts } from "utils/helpers/modal";
/**
 * `true` when the user opened the form via Edit after eligibility (same `/orders/edit/...` URL is used for new orders, so we do not key off pathname).
 */
export const useRequisitionOrderEditActive = (isElectronicFormOrder) => {
	const { state: locationState } = useLocation();
	return useMemo(
		() => Boolean(isElectronicFormOrder && locationState?.requisitionOrderEdit),
		[isElectronicFormOrder, locationState?.requisitionOrderEdit]
	);
};

/**
 * `DELETE /orders/{id}/requisition-draft` or `DELETE /internal-referral/{id}/requisition-draft` + redirect (or `back_to`).
 */
export const useRequisitionOrderCancel = ({ isElectronicFormOrder, isReferral = false, noteId }) => {
	const isRequisitionOrderEdit = useRequisitionOrderEditActive(isElectronicFormOrder);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { i18n, t } = useTranslation();
	const { search } = useLocation();
	const { back_to: backTo = "" } = qs.parse(search, { ignoreQueryPrefix: true });
	const [isCanceling, setIsCanceling] = useState(false);

	const runRequisitionOrderCancel = useCallback(
		(options = {}) => {
			if (!isRequisitionOrderEdit || !noteId) return;
			setIsCanceling(true);
			const deleteDraft = isReferral ? deleteInternalReferralRequisitionDraft : deleteOrderRequisitionDraft;
			apiHandler(deleteDraft, {
				data: {
					params: { id: noteId },
					options: { headers: { "x-prevent-flashes": "true" } },
				},
				onError: async (err) => {
					setIsCanceling(false);
					try {
						await errorResponseInterceptor(err, { dispatch, navigate, language: i18n.language, t });
					} catch {
						/* flashed by interceptor */
					}
				},
				onSuccess: async () => {
					setIsCanceling(false);
					clearModalArtifacts();
					dispatch(
						flashesActions.add({
							type: "info",
							message: t("requisition.remove_draft_done"),
							id: "requisition-cancel-order",
						})
					);
					const listPath =
						backTo ||
						`${getRouteLocale(i18n.language)}/user/lab-and-imaging/${isReferral ? "referrals" : "orders"}`;
					if (isFunction(options?.onBeforeNavigate)) {
						try {
							await options.onBeforeNavigate({ listPath });
						} catch {
							// ignore callback failures and continue navigation
						}
					}
					navigate(listPath, { replace: true });
				},
			});
		},
		[isRequisitionOrderEdit, noteId, isReferral, dispatch, t, backTo, i18n.language, navigate]
	);

	return { isRequisitionOrderEdit, isCanceling, runRequisitionOrderCancel };
};
